import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, KpiCard } from "@/components/Primitives";
import DataTable from "@/components/DataTable";
import RichEditor from "@/components/RichEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Ticket as TicketIcon, Plus, Bug, Wrench, Star, Sparkles, Eye, Pencil, X, RotateCcw, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { BranchTag, useBranches } from "@/components/BranchBits";

const TYPE_META = {
  bug:                 { label: "Bug",               color: "bg-red-500/15 text-red-500",       icon: Bug,     needsCRNDropdown: true },
  minor_improvement:   { label: "Minor Improvement", color: "bg-amber-500/15 text-amber-500",   icon: Wrench,  needsCRNDropdown: true },
  major_improvement:   { label: "Major Improvement", color: "bg-purple-500/15 text-purple-500", icon: Star,    needsCRNDropdown: false },
  new_functionality:   { label: "New Functionality", color: "bg-emerald-500/15 text-emerald-500", icon: Sparkles, needsCRNDropdown: false },
};

const STATUS_META = {
  pending:      { label: "Pending",     color: "bg-slate-500/15 text-slate-400" },
  in_progress:  { label: "In Progress", color: "bg-primary/15 text-primary" },
  resolved:     { label: "Resolved",    color: "bg-emerald-500/15 text-emerald-500" },
  closed:       { label: "Closed",      color: "bg-muted-foreground/15 text-muted-foreground" },
  rejected:     { label: "Rejected",    color: "bg-red-500/15 text-red-500" },
};

function fmtDateTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return isNaN(d) ? "—" : d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function TicketForm({ open, onOpenChange, editing, onSaved }) {
  const { user } = useAuth();
  const isClient = user?.role === "client";
  const isEdit = !!editing;
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);   // all visible projects
  const [crns, setCrns] = useState([]);
  const [assignables, setAssignables] = useState([]);
  const [form, setForm] = useState({
    client_id: "", project_id: "", type: "bug", crn: "", title: "", description: "",
    assignee_id: "", todos: [""],
  });
  const [saving, setSaving] = useState(false);

  // Reset + preload data when the dialog opens.
  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        client_id: editing.client_id || "",
        project_id: editing.project_id || "",
        type: editing.type || "bug",
        crn: editing.crn || "",
        title: editing.title || "",
        description: editing.description || "",
        assignee_id: editing.assignee_id || "",
        todos: [""], // checklist not editable via edit dialog — managed on detail page
      });
    } else {
      setForm({ client_id: "", project_id: "", type: "bug", crn: "", title: "", description: "", assignee_id: "", todos: [""] });
    }
    setCrns([]); setAssignables([]);
    if (isClient) {
      const cid = user?.client_id;
      if (cid && !isEdit) setForm((f) => ({ ...f, client_id: cid }));
      api.get(`/clients/${cid}`).then((r) => setClients([r.data])).catch(() => setClients([]));
    } else {
      api.get("/clients").then((r) => setClients(r.data)).catch(() => setClients([]));
    }
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => setProjects([]));
  }, [open, isClient, user?.client_id, isEdit, editing]);

  const projectsForClient = useMemo(
    () => projects.filter((p) => p.client_id === form.client_id),
    [projects, form.client_id]
  );

  // When project or type changes, refresh the CRN dropdown for bug/minor.
  useEffect(() => {
    if (!isEdit) setForm((f) => ({ ...f, crn: "" }));
    if (!form.project_id) { setCrns([]); return; }
    if (TYPE_META[form.type]?.needsCRNDropdown) {
      api.get(`/tickets/projects/${form.project_id}/crns`).then((r) => setCrns(r.data)).catch(() => setCrns([]));
    }
  }, [form.project_id, form.type, isEdit]);

  // Load assignable users whenever project changes.
  useEffect(() => {
    if (!form.project_id) { setAssignables([]); return; }
    api.get(`/tickets/projects/${form.project_id}/assignable-users`).then((r) => setAssignables(r.data)).catch(() => setAssignables([]));
    // Clear assignee if switching project.
    if (!isEdit) setForm((f) => ({ ...f, assignee_id: "" }));
  }, [form.project_id, isEdit]);

  const submit = async () => {
    if (!form.client_id) return toast.error("Pick a client");
    if (!form.project_id) return toast.error("Pick a project");
    if (!form.crn.trim()) return toast.error("CRN is required");
    if (!form.title.trim()) return toast.error("Title is required");
    if (form.title.trim().length > 160) return toast.error("Title must be 160 characters or fewer");
    const plain = (form.description || "").replace(/<[^>]+>/g, "").trim();
    if (!plain) return toast.error("Description is required");
    setSaving(true);
    try {
      if (isEdit) {
        // Server only accepts these fields — checklist edits go through detail page.
        await api.patch(`/tickets/${editing.id}`, {
          type: form.type, crn: form.crn, title: form.title.trim(),
          description: form.description, assignee_id: form.assignee_id || null,
        });
        toast.success("Ticket updated");
      } else {
        await api.post("/tickets", {
          ...form,
          assignee_id: form.assignee_id || null,
          todos: form.todos.map((t) => t.trim()).filter(Boolean),
        });
        toast.success("Ticket created");
      }
      onOpenChange(false);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save ticket");
    } finally {
      setSaving(false);
    }
  };

  const clientLocked = isClient;
  const projectLocked = isEdit;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="ticket-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit ${editing.ticket_number}` : "New Ticket"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Only allowed while status is Pending." : "Raise a ticket against a project you have access to."}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Client *</Label>
              <Select value={form.client_id} disabled={clientLocked || projectLocked} onValueChange={(v) => setForm({ ...form, client_id: v, project_id: "" })}>
                <SelectTrigger data-testid="ticket-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No clients available</div>}
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
              {clientLocked && <div className="text-[10px] text-muted-foreground mt-1">Locked to your organisation</div>}
            </div>
            <div>
              <Label>Project *</Label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })} disabled={!form.client_id || projectLocked}>
                <SelectTrigger data-testid="ticket-project"><SelectValue placeholder={form.client_id ? "Select project" : "Pick client first"} /></SelectTrigger>
                <SelectContent>
                  {projectsForClient.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No accessible projects for this client</div>}
                  {projectsForClient.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} <span className="opacity-60 text-xs">· {p.code}</span></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Type *</Label>
              <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                <SelectTrigger data-testid="ticket-type"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(TYPE_META).map(([k, v]) => (
                    <SelectItem key={k} value={k}>{v.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>CRN *</Label>
              {TYPE_META[form.type]?.needsCRNDropdown ? (
                <Select value={form.crn} onValueChange={(v) => setForm({ ...form, crn: v })} disabled={!form.project_id}>
                  <SelectTrigger data-testid="ticket-crn-select"><SelectValue placeholder={form.project_id ? (crns.length ? "Pick a CRN" : "No existing CRNs — pick a different type") : "Pick project first"} /></SelectTrigger>
                  <SelectContent>
                    {crns.length === 0 && <div className="px-2 py-1.5 text-xs text-muted-foreground">No CRNs yet on this project</div>}
                    {crns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              ) : (
                <Input data-testid="ticket-crn-input" value={form.crn} onChange={(e) => setForm({ ...form, crn: e.target.value })} placeholder="e.g. CRN-2026-014" />
              )}
              <div className="text-[10px] text-muted-foreground mt-1">
                {TYPE_META[form.type]?.needsCRNDropdown
                  ? "Bug / Minor Improvement — must reference an existing CRN on this project."
                  : "Major Improvement / New Functionality — enter a new CRN."}
              </div>
            </div>
          </div>

          <div>
            <Label>Assignee (optional)</Label>
            <Select value={form.assignee_id || "__none__"} onValueChange={(v) => setForm({ ...form, assignee_id: v === "__none__" ? "" : v })} disabled={!form.project_id}>
              <SelectTrigger data-testid="ticket-assignee"><SelectValue placeholder={form.project_id ? "Pick project member" : "Pick project first"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">Unassigned</SelectItem>
                {assignables.map((a) => <SelectItem key={a.id} value={a.id}>{a.full_name}{a.designation ? ` · ${a.designation}` : ""}</SelectItem>)}
                {assignables.length === 0 && form.project_id && <div className="px-2 py-1.5 text-xs text-muted-foreground">No members on this project</div>}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>Title *</Label>
            <Input
              data-testid="ticket-title"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="One-line summary (shows in the ticket list)"
              maxLength={160}
            />
            <div className="text-[10px] text-muted-foreground mt-0.5">{form.title.length}/160</div>
          </div>

          <div>
            <Label>Description *</Label>
            <RichEditor
              testid="ticket-description"
              value={form.description}
              onChange={(html) => setForm((f) => ({ ...f, description: html }))}
              placeholder="Describe the issue or ask in detail — use lists, headings and formatting to keep it organised. You can paste screenshots directly into this box."
              minHeight={180}
            />
          </div>

          {!isEdit && (
            <div>
              <Label>Checklist (optional)</Label>
              <div className="text-[10px] text-muted-foreground mb-1">The ticket status will progress automatically as items are ticked off.</div>
              <div className="space-y-1">
                {form.todos.map((t, idx) => (
                  <div key={idx} className="flex items-center gap-2">
                    <Input value={t} placeholder={`Checklist item #${idx + 1}`} data-testid={`ticket-todo-${idx}`} onChange={(e) => setForm((f) => { const n = [...f.todos]; n[idx] = e.target.value; return { ...f, todos: n }; })} />
                    <Button size="icon" variant="ghost" onClick={() => setForm((f) => ({ ...f, todos: f.todos.filter((_, i) => i !== idx).length ? f.todos.filter((_, i) => i !== idx) : [""] }))}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button variant="outline" size="sm" onClick={() => setForm((f) => ({ ...f, todos: [...f.todos, ""] }))} data-testid="ticket-add-todo">
                  <Plus className="w-4 h-4 mr-1" />Add checklist item
                </Button>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button data-testid="ticket-submit" onClick={submit} disabled={saving}>{saving ? (isEdit ? "Saving…" : "Creating…") : (isEdit ? "Save changes" : "Create ticket")}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function initialsOf(name) { return (name || "?").split(" ").map((s) => s[0]).slice(0, 2).join(""); }

export default function Tickets() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user?.role === "admin";
  const { byId: branchById } = useBranches();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/tickets").then((r) => setRows(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const kpis = useMemo(() => ({
    total: rows.length,
    pending: rows.filter((t) => t.status === "pending").length,
    in_progress: rows.filter((t) => t.status === "in_progress").length,
    resolved: rows.filter((t) => t.status === "resolved").length,
  }), [rows]);

  const canEdit = (r) => (isAdmin || r.created_by === user?.id) && r.status === "pending";

  const columns = [
    { key: "ticket_number", label: "Ticket", sortable: true, render: (r) => (
      <div>
        <div className="font-medium text-sm">{r.ticket_number}</div>
        <div className="text-[10px] text-muted-foreground truncate max-w-[240px]">{r.crn}</div>
      </div>
    ) },
    { key: "title", label: "Title", sortable: true, render: (r) => (
      <div className="font-medium text-sm text-foreground truncate max-w-[380px]" title={r.title || ""}>
        {r.title || <span className="text-muted-foreground italic">Untitled</span>}
      </div>
    ) },
    { key: "type", label: "Type", sortable: true, render: (r) => {
      const m = TYPE_META[r.type] || {};
      const Icon = m.icon || TicketIcon;
      return <Badge className={`${m.color || ""} border-0 rounded-md text-[10px]`}><Icon className="w-3 h-3 mr-1" />{m.label || r.type}</Badge>;
    } },
    { key: "client_name", label: "Client", sortable: true, render: (r) => r.client_name || <span className="text-muted-foreground">—</span> },
    { key: "project_name", label: "Project", sortable: true, render: (r) => (
      <div className="text-sm">
        <div className="truncate max-w-[220px]">{r.project_name}</div>
        <div className="text-[10px] text-muted-foreground">{r.project_code}</div>
      </div>
    ) },
    { key: "assignee_name", label: "Assignee", sortable: true, render: (r) => (
      r.assignee_name ? (
        <div className="flex items-center gap-2">
          <Avatar className="h-6 w-6"><AvatarImage src={r.assignee_avatar_url || ""} /><AvatarFallback className="text-[8px]">{initialsOf(r.assignee_name)}</AvatarFallback></Avatar>
          <span className="text-sm truncate max-w-[140px]">{r.assignee_name}</span>
        </div>
      ) : <span className="text-muted-foreground text-xs">Unassigned</span>
    ) },
    { key: "completion_percentage", label: "Progress", sortable: true, render: (r) => (
      <div className="min-w-[140px]">
        <div className="flex items-center gap-2">
          <Progress value={r.completion_percentage || 0} className="h-1.5 flex-1" />
          <span className="number text-xs">{r.completion_percentage || 0}%</span>
        </div>
        <div className="text-[10px] text-muted-foreground mt-0.5">
          {(r.completed_todo_count || 0)}/{(r.todo_count || 0)} items
          {r.reopen_count ? <> · <RotateCcw className="inline w-2.5 h-2.5" /> {r.reopen_count} reopen{r.reopen_count > 1 ? "s" : ""}</> : null}
        </div>
      </div>
    ) },
    { key: "status", label: "Status", sortable: true, render: (r) => {
      const m = STATUS_META[r.status] || {};
      return <Badge className={`${m.color || ""} border-0 rounded-md text-[10px]`}>{m.label || r.status}</Badge>;
    } },
    { key: "branch_id", label: "Branch", sortable: true, render: (r) => <BranchTag id={r.branch_id} byId={branchById} /> },
    { key: "created_by_name", label: "Raised by", sortable: true, render: (r) => (
      <span className="text-sm">{r.created_by_name || "—"}{r.created_by_role === "client" && <span className="text-[10px] text-muted-foreground"> · client</span>}</span>
    ) },
    { key: "created_at", label: "Created", sortable: true, render: (r) => (
      <span className="text-xs text-muted-foreground whitespace-nowrap">{fmtDateTime(r.created_at)}</span>
    ) },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        testid="tickets-header"
        title="Tickets"
        subtitle={`${rows.length} tickets across your projects`}
        actions={
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) setEditing(null); }}>
            <DialogTrigger asChild>
              <Button data-testid="new-ticket-btn" onClick={() => setEditing(null)}><Plus className="w-4 h-4 mr-1" />New Ticket</Button>
            </DialogTrigger>
            <TicketForm open={open} onOpenChange={setOpen} editing={editing} onSaved={load} />
          </Dialog>
        }
      />
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <KpiCard label="All Tickets" value={kpis.total} icon={TicketIcon} tone="primary" />
        <KpiCard label="Pending" value={kpis.pending} />
        <KpiCard label="In Progress" value={kpis.in_progress} />
        <KpiCard label="Resolved" value={kpis.resolved} tone="success" />
      </div>
      <DataTable
        testid="tickets-table"
        columns={columns}
        data={rows}
        loading={loading}
        empty="No tickets yet — raise your first one to get started."
        searchKeys={["ticket_number", "title", "crn", "client_name", "project_name", "project_code", "created_by_name", "assignee_name"]}
        initialPageSize={10}
        rowActions={(r) => (
          <div className="flex items-center justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button size="icon" variant="ghost" className="h-8 w-8" data-testid={`ticket-actions-${r.id}`} title="Actions">
                  <MoreVertical className="w-4 h-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                <DropdownMenuItem data-testid={`ticket-view-${r.id}`} onClick={() => nav(`/tickets/${r.id}`)}>
                  <Eye className="w-4 h-4 mr-2" />View
                </DropdownMenuItem>
                {canEdit(r) && (
                  <DropdownMenuItem data-testid={`ticket-edit-${r.id}`} onClick={() => { setEditing(r); setOpen(true); }}>
                    <Pencil className="w-4 h-4 mr-2" />Edit
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )}
      />
    </div>
  );
}
